import { FC, useCallback, useEffect, useState } from "react";

import { ContentCopy, Send } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ConnectInvitationWithJoinUrl,
  getConnectInvitations,
  postConnectInvitation,
  postConnectInvitationCopied,
  postConnectInvitationSend,
} from "@rubi/api-generated/clientRs";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

const ROLES = [
  { value: "victim", labelKey: "invitations.roles.victim" },
  { value: "translator", labelKey: "invitations.roles.translator" },
  { value: "observer", labelKey: "invitations.roles.observer" },
  { value: "other", labelKey: "invitations.roles.other" },
] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  meetingId: string | null;
  meetingTitle?: string;
}

const InvitationsDialog: FC<Props> = ({
  open,
  onClose,
  orgId,
  meetingId,
  meetingTitle,
}) => {
  const { t } = useTranslation("connect");
  const [invitations, setInvitations] = useState<
    ConnectInvitationWithJoinUrl[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [role, setRole] = useState("");
  const [otherDescription, setOtherDescription] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [dobUnknown, setDobUnknown] = useState(false);
  const [label, setLabel] = useState("");

  const load = useCallback(async () => {
    if (!meetingId) return;
    setLoading(true);
    try {
      const res = await getConnectInvitations({
        path: { org_id: orgId, meeting_id: meetingId },
      });
      setInvitations(res.data?.invitations ?? []);
    } catch {
      toast.error(t("invitations.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [orgId, meetingId, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const resetForm = () => {
    setRole("");
    setOtherDescription("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setDob("");
    setDobUnknown(false);
    setLabel("");
  };

  const combinedName = `${firstName.trim()} ${lastName.trim()}`.trim();
  // DOB is only meaningful for the interview subject; observers/translators are
  // staff-adjacent, so hide it (matches the officer setup mockups).
  const showDob = role !== "observer";
  const hasEmail = email.trim().length > 0;
  const hasPhone = phone.trim().length > 0;
  const otherOk = role !== "other" || otherDescription.trim().length > 0;
  const canSubmit =
    !!role &&
    combinedName.length > 0 &&
    (hasEmail || hasPhone) &&
    otherOk &&
    !submitting;

  const handleAdd = async (sendVia?: "email" | "sms" | "both") => {
    if (!meetingId || !canSubmit) return;
    setSubmitting(true);
    try {
      const res = await postConnectInvitation({
        path: { org_id: orgId, meeting_id: meetingId },
        body: {
          role,
          other_description:
            role === "other" ? otherDescription.trim() : undefined,
          name: combinedName,
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          dob: showDob && !dobUnknown ? dob.trim() || undefined : undefined,
          dob_unknown: showDob ? dobUnknown : undefined,
          label: label.trim() || undefined,
          send_via: sendVia,
        },
      });
      const joinUrl = res.data?.join_url;
      const created = res.data?.invitation;
      if (!sendVia && joinUrl) {
        try {
          await navigator.clipboard.writeText(joinUrl);
          if (created) recordLinkCopied(created.id);
        } catch {
          /* clipboard may be unavailable */
        }
      }
      toast.success(
        sendVia
          ? t("invitations.toast.createdAndSent")
          : t("invitations.toast.createdAndCopied"),
      );
      resetForm();
      await load();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.console_msg ?? t("invitations.toast.createFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // A copied link leaves RubiCONNECT by some other channel, so the copy itself
  // is the delivery event the audit trail records.
  const recordLinkCopied = (invitationId: string) => {
    if (!meetingId) return;
    postConnectInvitationCopied({
      path: {
        org_id: orgId,
        meeting_id: meetingId,
        invitation_id: invitationId,
      },
    }).catch(() => {
      /* audit is best-effort from the client */
    });
  };

  const copyLink = async (inv: ConnectInvitationWithJoinUrl) => {
    try {
      await navigator.clipboard.writeText(inv.join_url);
      recordLinkCopied(inv.id);
      toast.success(t("invitations.toast.linkCopied"));
    } catch {
      toast.error(t("invitations.toast.copyFailed"));
    }
  };

  const send = async (invitationId: string, via: "email" | "sms") => {
    if (!meetingId) return;
    try {
      await postConnectInvitationSend({
        path: {
          org_id: orgId,
          meeting_id: meetingId,
          invitation_id: invitationId,
        },
        body: { via },
      });
      toast.success(t("invitations.toast.sent"));
      await load();
    } catch {
      toast.error(t("invitations.toast.sendFailed"));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {meetingTitle
          ? t("invitations.titleWithMeeting", { title: meetingTitle })
          : t("invitations.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" gutterBottom>
          {t("invitations.form.heading")}
        </Typography>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <FormControl fullWidth required size="small">
            <InputLabel id="connect-invite-role">
              {t("invitations.form.roleLabel")}
            </InputLabel>
            <Select
              labelId="connect-invite-role"
              label={t("invitations.form.roleLabel")}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {t(r.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {role === "other" && (
            <TextField
              label={t("invitations.form.otherDescriptionLabel")}
              required
              fullWidth
              size="small"
              value={otherDescription}
              onChange={(e) => setOtherDescription(e.target.value)}
            />
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label={t("invitations.form.firstNameLabel")}
              required
              fullWidth
              size="small"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextField
              label={t("invitations.form.lastNameLabel")}
              fullWidth
              size="small"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label={t("invitations.form.emailLabel")}
              type="email"
              fullWidth
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!hasEmail && !hasPhone}
              helperText={
                !hasEmail && !hasPhone
                  ? t("invitations.form.contactRequired")
                  : " "
              }
            />
            <TextField
              label={t("invitations.form.phoneLabel")}
              fullWidth
              size="small"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={!hasEmail && !hasPhone}
              helperText={
                !hasEmail && !hasPhone
                  ? t("invitations.form.contactRequired")
                  : " "
              }
            />
          </Stack>
          {showDob && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
            >
              <TextField
                label={t("invitations.form.dobLabel")}
                placeholder={t("invitations.form.dobPlaceholder")}
                fullWidth
                size="small"
                value={dobUnknown ? "" : dob}
                disabled={dobUnknown}
                onChange={(e) => setDob(e.target.value)}
              />
              <FormControlLabel
                sx={{ whiteSpace: "nowrap", m: 0 }}
                control={
                  <Checkbox
                    checked={dobUnknown}
                    onChange={(e) => setDobUnknown(e.target.checked)}
                  />
                }
                label={t("invitations.form.dobUnknown")}
              />
            </Stack>
          )}
          <TextField
            label={t("invitations.form.partyLabel")}
            fullWidth
            size="small"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              disabled={!canSubmit}
              onClick={() => handleAdd()}
            >
              {t("invitations.form.addAndCopy")}
            </Button>
            <Button
              variant="outlined"
              disabled={!canSubmit || !hasEmail}
              onClick={() => handleAdd("email")}
            >
              {t("invitations.form.addAndEmail")}
            </Button>
            <Button
              variant="outlined"
              disabled={!canSubmit || !hasPhone}
              onClick={() => handleAdd("sms")}
            >
              {t("invitations.form.addAndText")}
            </Button>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          {t("invitations.list.heading")}
        </Typography>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : invitations.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            {t("invitations.list.empty")}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {invitations.map((inv) => {
              const contact =
                inv.email || inv.phone || t("invitations.list.noContact");
              return (
                <Box
                  key={inv.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2">
                      <strong>{inv.name}</strong>
                      {inv.label
                        ? t("invitations.list.labelSuffix", {
                            label: inv.label,
                          })
                        : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {inv.sent_at
                        ? inv.delivery_method
                          ? t("invitations.list.sentVia", {
                              contact,
                              method: inv.delivery_method,
                            })
                          : t("invitations.list.sent", { contact })
                        : t("invitations.list.notSent", { contact })}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={t(`roles.${inv.role}`, { defaultValue: inv.role })}
                  />
                  <Chip size="small" variant="outlined" label={inv.status} />
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={() => copyLink(inv)}
                  >
                    {t("invitations.list.copyLink")}
                  </Button>
                  {inv.email && (
                    <Button
                      size="small"
                      startIcon={<Send />}
                      onClick={() => send(inv.id, "email")}
                    >
                      {t("invitations.list.sendEmail")}
                    </Button>
                  )}
                  {inv.phone && (
                    <Button
                      size="small"
                      startIcon={<Send />}
                      onClick={() => send(inv.id, "sms")}
                    >
                      {t("invitations.list.sendText")}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("invitations.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvitationsDialog;
